import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useEditorStore } from '../store';
import { computeBlastRadius } from './blastRadius';

interface BlastRadiusContextValue {
  /** Currently-focused node ID, or null when off. */
  focus: string | null;
  /** Nodes that break if focus fails — empty set when focus is null. */
  affected: Set<string>;
  /** Diagram-wide SPOF set; populated even when focus is null so the
   * editor can show permanent ⚠ badges on critical nodes. */
  spofs: Set<string>;
}

const EMPTY_SET: Set<string> = new Set();

const Ctx = createContext<BlastRadiusContextValue>({
  focus: null,
  affected: EMPTY_SET,
  spofs: EMPTY_SET,
});

/**
 * BlastRadiusProvider computes the affected + SPOF sets once per
 * (nodes, edges, focus) change and broadcasts them via context so every
 * CloudNode doesn't re-traverse the graph on each render. The traversal
 * itself is O(V + E) — cheap for ≤100 nodes — but doing it in every
 * memo cycle of every node would add up at scale.
 *
 * Mount this anywhere above both the canvas (where nodes consume the
 * tint) and the right-side properties panel (where the summary lives).
 */
export function BlastRadiusProvider({ children }: { children: ReactNode }) {
  const nodes = useEditorStore((s) => s.nodes);
  const edges = useEditorStore((s) => s.edges);
  const focus = useEditorStore((s) => s.blastRadiusFocus);
  const selectedNodeId = useEditorStore((s) => s.selectedNodeId);
  const setBlastRadiusFocus = useEditorStore((s) => s.setBlastRadiusFocus);

  // When blast mode is on AND the user clicks a different node, follow
  // the selection. This is the "click around to see each node's blast"
  // demo gesture — without it the user has to re-click "What breaks?"
  // for every node, which kills the flow. Clearing blast happens via
  // the explicit Exit button or Escape, never silently.
  useEffect(() => {
    if (focus !== null && selectedNodeId !== null && selectedNodeId !== focus) {
      setBlastRadiusFocus(selectedNodeId);
    }
    // We intentionally don't include `focus` in deps — when WE change the
    // focus here, we don't want to re-fire the effect against ourselves.
    // The effect only matters when selection changes while blast is on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, setBlastRadiusFocus]);

  const value = useMemo<BlastRadiusContextValue>(() => {
    // When focus is null we still compute SPOFs across the whole graph so
    // the ⚠ badges show without requiring the user to enter blast mode.
    // Pass a sentinel '' as source — it won't match any real node and the
    // affected set stays empty, while the SPOF loop runs over every node.
    const r = computeBlastRadius(nodes, edges, focus ?? '');
    return {
      focus,
      affected: focus ? r.affected : EMPTY_SET,
      spofs: r.spofs,
    };
  }, [nodes, edges, focus]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBlastRadius(): BlastRadiusContextValue {
  return useContext(Ctx);
}
